/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import MuiLink from '@material-ui/core/Link'
import { FormattedMessage, useIntl } from 'react-intl'
import Grid from '@material-ui/core/Grid'

const useCustomStyles = makeStyles((theme) => ({
  legalLinks: {
    display: 'flex',
    textAlign: 'left',
    padding: theme.spacing(2, 4, 3),
    '& a': {
      color: theme.palette.textPrimary.main,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 0.5),
      '& div:not(:first-child)': {
        marginTop: theme.spacing(1),
      },
    },
  },
  linkGroup: {
    width: 330,
    padding: theme.spacing(0, 2),
    fontSize: 12,
  },
  series: {
    width: 330,
    padding: theme.spacing(0, 2),
    fontSize: 12,
    '& a:first-child': {
      paddingRight: theme.spacing(1),
    },
    '& a:not(:first-child)': {
      padding: theme.spacing(0, 1),
      borderLeft: `1px solid ${theme.palette.textPrimary.main}`,
    },
  },
  span: {
    display: 'block',
  },
}))

/**
 * Render the legal links section in the footer.
 * @returns {Node} - a node
 */
function LegalLinks() {
  const classes = useCustomStyles()
  const intl = useIntl()
  const currentYear = useMemo(() => new Date().getFullYear(), [])

  const copyrightMessage = (
    <FormattedMessage
      id='copyright'
      defaultMessage='Copyright © 2001-{currentYear} OCLC.'
      values={{ currentYear: currentYear }}
    />
  )

  // prettier-ignore
  const messages = {
    rightsMessage: intl.formatMessage({id: 'all_rights_reserved', defaultMessage: 'All rights reserved.'}),
    privacyMessage: intl.formatMessage({id: 'privacy_policy', defaultMessage: 'Privacy Policy'}),
    termsMessage: intl.formatMessage({id: 'terms_and_conditions', defaultMessage: 'Terms and Conditions'}),
    tmMessage: intl.formatMessage({id: 'trademark', defaultMessage: 'Domestic and international trademarks and/or service marks of OCLC, Inc. and its affiliates.'}),
    cookieMessage: intl.formatMessage({id: 'cookie_notice', defaultMessage: 'Cookie Notice'}),
    a11yMessage: intl.formatMessage({id: 'a11y_statement', defaultMessage: 'Accessibility Statement'}),
    isoMessage: intl.formatMessage({id: 'iso_certificate', defaultMessage: 'ISO 27001 Certificate'}),
  }

  return (
    <Grid container className={classes.legalLinks}>
      <Grid item className={classes.linkGroup} xs={12} sm={4}>
        <MuiLink
          variant='caption'
          underline='none'
          data-testid='copyright-link'
          target='_blank'
          href={'https://policies.oclc.org/en/copyright.html'}>
          <span className={classes.span}>{copyrightMessage}</span>
          <span>{messages.rightsMessage}</span>
        </MuiLink>
      </Grid>
      <Grid item xs={12} sm={4} className={classes.linkGroup}>
        <MuiLink
          variant='caption'
          underline='none'
          data-testid='trademark-link'
          target='_blank'
          href={'https://policies.oclc.org/en/copyright/trademarks.html'}>
          {messages.tmMessage}
        </MuiLink>
      </Grid>
      <Grid item xs={12} sm={4} className={classes.series}>
        <MuiLink
          variant='caption'
          underline='always'
          data-testid='terms-and-condition-link'
          target='_blank'
          href='https://www.oclc.org/content/dam/ext-ref/worldcat-org/terms.html'>
          {messages.termsMessage}
        </MuiLink>
        <MuiLink
          variant='caption'
          underline='always'
          data-testid='privacy-policy-link'
          target='_blank'
          href={'https://policies.oclc.org/en/privacy/privacy-statement.html'}>
          {messages.privacyMessage}
        </MuiLink>
        <MuiLink
          variant='caption'
          underline='always'
          data-testid='cookie-notice-link'
          target='_blank'
          href='https://policies.oclc.org/en/privacy/cookie-statement.html'>
          {messages.cookieMessage}
        </MuiLink>
        <MuiLink
          variant='caption'
          underline='always'
          data-testid='accessibility-link'
          target='_blank'
          href='https://policies.oclc.org/en/accessibility.html'>
          {messages.a11yMessage}
        </MuiLink>
        <MuiLink
          variant='caption'
          underline='always'
          data-testid='iso-link'
          target='_blank'
          href='https://policies.oclc.org/en/security/iso-27001.html'>
          {messages.isoMessage}
        </MuiLink>
      </Grid>
    </Grid>
  )
}

export default LegalLinks
